import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import ShareModal from "../components/ShareModal";

export default function Dashboard() {
  const { user } = useAuth();
  const { t, pick } = useI18n();
  const [result, setResult] = useState(null);
  const [question, setQuestion] = useState(null);
  const [myVote, setMyVote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [shareOpen, setShareOpen] = useState(false);

  const load = async () => {
    try {
      const [r, mv, q] = await Promise.all([
        api.get("/results"), api.get("/vote/mine"), api.get("/questions"),
      ]);
      const q1 = r.data.results.find((x) => x.id === "q1");
      const q1q = q.data.questions.find((x) => x.id === "q1");
      setResult(q1); setQuestion(q1q); setMyVote(mv.data.votes?.q1 || null);
      setError("");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-[#F4F4F4]" data-testid="dashboard-loading">
        <div className="font-mono-civic text-sm uppercase tracking-widest text-black">{t("dash_loading")}</div>
      </div>
    );
  }
  if (!result) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-[#F4F4F4]">
        <div className="font-mono-civic text-sm uppercase tracking-widest text-black">{t("dash_no_data")}</div>
      </div>
    );
  }

  const { yes, no, total, yes_pct: yesPct, no_pct: noPct } = result;
  const leader = total === 0 ? null : yes >= no ? "YES" : "NO";

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F4F4F4] text-black px-5 py-10" data-testid="dashboard-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-black flex items-center gap-2">
              <span className="w-2 h-2 bg-[#E00000] rounded-full animate-pulse" />
              {t("dash_headline_tag")}
            </div>
            <h1 className="mt-2 font-serif-display text-black leading-[0.9] text-5xl sm:text-6xl" style={{ fontWeight: 800 }} data-testid="dashboard-welcome">
              {t("dash_welcome")}{user?.email ? `, ${user.email.split("@")[0]}` : ""}.
            </h1>
          </div>
          <div className="text-right">
            <div className="font-mono-civic text-xs uppercase tracking-widest text-black">{t("dash_total")}</div>
            <div className="font-serif-display text-4xl text-black leading-none" style={{ fontWeight: 800 }} data-testid="dashboard-total-votes">
              {total.toLocaleString()}
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-6 border-2 border-[#E00000] bg-white p-3 text-[#E00000] text-sm" style={{ fontWeight: 800 }} data-testid="dashboard-error">{error}</div>
        )}

        <div className="mt-8 bg-white border-2 border-black brutal-shadow-red p-6 sm:p-10" data-testid="dashboard-headline-card">
          <div className="font-mono-civic text-[10px] uppercase tracking-widest bg-[#E00000] text-white inline-block px-2 py-1 mb-4" style={{ fontWeight: 900 }}>
            {t("dash_headline_q_tag")}
          </div>
          <p className="font-serif-display italic text-black leading-[1.02] text-4xl sm:text-5xl md:text-6xl" style={{ fontWeight: 800 }}>
            {t("dash_headline_q")}
          </p>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 sm:gap-6">
          <div className="bg-white border-2 border-black brutal-shadow p-6 sm:p-10 text-center" data-testid="dashboard-yes-box">
            <div className="font-mono-civic text-xs sm:text-sm uppercase tracking-[0.3em] text-black" style={{ fontWeight: 900 }}>{t("dash_left_yes")}</div>
            <div className="mt-3 font-serif-display text-black leading-none text-[20vw] sm:text-[14vw] md:text-[10rem]" style={{ fontWeight: 800 }} data-testid="dashboard-yes-count">
              {yes.toLocaleString()}
            </div>
            <div className="mt-4 inline-block bg-black text-white font-mono-civic text-xs sm:text-sm uppercase tracking-widest px-4 py-2" style={{ fontWeight: 900 }}>
              {t("q_yes")} · {yesPct}%
            </div>
          </div>
          <div className="bg-white border-2 border-black brutal-shadow p-6 sm:p-10 text-center" data-testid="dashboard-no-box">
            <div className="font-mono-civic text-xs sm:text-sm uppercase tracking-[0.3em] text-black" style={{ fontWeight: 900 }}>{t("dash_right_no")}</div>
            <div className="mt-3 font-serif-display text-[#E00000] leading-none text-[20vw] sm:text-[14vw] md:text-[10rem]" style={{ fontWeight: 800 }} data-testid="dashboard-no-count">
              {no.toLocaleString()}
            </div>
            <div className="mt-4 inline-block bg-[#E00000] text-white font-mono-civic text-xs sm:text-sm uppercase tracking-widest px-4 py-2" style={{ fontWeight: 900 }}>
              {t("q_no")} · {noPct}%
            </div>
          </div>
        </div>

        {total > 0 && (
          <div className="mt-6 border-2 border-black bg-white p-3" data-testid="dashboard-proportion-bar">
            <div className="flex h-8 border-2 border-black overflow-hidden">
              <div className="bg-black flex items-center justify-center text-white font-mono-civic text-xs uppercase tracking-widest" style={{ width: `${yesPct}%`, fontWeight: 900 }}>
                {yesPct >= 12 ? `${t("q_yes")} ${yesPct}%` : ""}
              </div>
              <div className="bg-[#E00000] flex items-center justify-center text-white font-mono-civic text-xs uppercase tracking-widest" style={{ width: `${noPct}%`, fontWeight: 900 }}>
                {noPct >= 12 ? `${noPct}% ${t("q_no")}` : ""}
              </div>
            </div>
            {leader && (
              <div className="mt-3 font-mono-civic text-xs uppercase tracking-widest text-black text-center" style={{ fontWeight: 900 }} data-testid="dashboard-leader">
                {leader === "YES" ? t("q_yes") : t("q_no")} {t("dash_leader")} {Math.abs(yesPct - noPct).toFixed(1)} {t("dash_points")}
              </div>
            )}
          </div>
        )}

        <div className="mt-8 bg-white border-2 border-black brutal-shadow-sm p-5 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="dashboard-your-vote">
          <div>
            <div className="font-mono-civic text-xs uppercase tracking-widest text-black">{t("dash_your_vote")}</div>
            <div className="font-serif-display text-3xl text-black mt-1" style={{ fontWeight: 800 }}>
              {myVote ? (
                <span>
                  {t("dash_you_voted")} <span className={myVote === "YES" ? "text-black" : "text-[#E00000]"}>{myVote === "YES" ? t("q_yes") : t("q_no")}</span>
                </span>
              ) : (
                <span className="opacity-70">{t("dash_no_vote_yet")}</span>
              )}
            </div>
          </div>
          <Link to="/vote/q1"
            className="bg-black text-white uppercase tracking-widest px-6 py-4 border-2 border-black brutal-shadow-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all whitespace-nowrap"
            style={{ fontWeight: 900 }} data-testid="dashboard-cast-vote-btn">
            {myVote ? t("dash_change_vote") : t("dash_cast_vote")} →
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <button onClick={() => setShareOpen(true)} disabled={!result || result.total === 0}
            className="bg-[#E00000] text-white uppercase tracking-widest px-6 py-5 border-2 border-[#E00000] hover:bg-white hover:text-[#E00000] hover:border-[#E00000] transition-colors text-center disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ fontWeight: 900 }} data-testid="dashboard-share-btn">
            {result && result.total > 0 ? t("dash_share_btn") : t("dash_share_no")}
          </button>
          <Link to="/vote"
            className="bg-white text-black uppercase tracking-widest px-6 py-5 border-2 border-black hover:bg-black hover:text-white transition-colors text-center"
            style={{ fontWeight: 800 }} data-testid="dashboard-all-questions">
            {t("dash_all_questions")}
          </Link>
          <Link to="/results"
            className="bg-white text-black uppercase tracking-widest px-6 py-5 border-2 border-black hover:bg-black hover:text-white transition-colors text-center"
            style={{ fontWeight: 800 }} data-testid="dashboard-full-results">
            {t("dash_full_results")}
          </Link>
        </div>

        <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} question={question} result={result} />

        <div className="mt-10 border-t-2 border-black pt-6 font-mono-civic text-[10px] uppercase tracking-widest text-black/60" data-testid="dashboard-disclaimer">
          {t("dash_disclaimer")}
        </div>
      </div>
    </div>
  );
}
